import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth } from './AuthContext';
import { useFamily } from './FamilyContext';
import {
  subscribeToHydrationGroups,
  createHydrationGroup as svcCreateHydrationGroup,
  updateHydrationGroup as svcUpdateHydrationGroup,
  softDeleteHydrationGroup as svcSoftDeleteHydrationGroup,
  waterHydrationGroup as svcWaterHydrationGroup,
} from '../services/hydrationGroupService';

export const HydrationGroupsContext = createContext(null);

/**
 * Provider de baldes de hidratación del hogar activo.
 *
 * Diseño: NO depende de PetsContext. La operación waterHydrationGroup
 * recibe el array de pets afectadas como input (el caller lo arma desde
 * usePets() o desde fixtures de test). Esto mantiene los providers
 * desacoplados y evita ordenar mountings (Pets podría montarse después).
 *
 * Bug histórico que motivó el visibility listener: cuando Chrome cierra y
 * reabre el tab, Firestore con `persistentLocalCache` devuelve PRIMERO un
 * snapshot del cache local. Si la colección es nueva (hydrationGroups
 * recién deployada), el cache está vacío. El snapshot del server debería
 * llegar después, pero el subscribe puede "dormirse" en el tab background
 * y nunca emitir el segundo snapshot. Sin un re-subscribe forzado al
 * volver a foreground, la UI queda con `groups: []` hasta que el usuario
 * hace F5. El effect de visibilitychange dispara un tear-down/setup del
 * subscribe que sí lee del server.
 */
export const HydrationGroupsProvider = ({ children }) => {
  const { profile } = useAuth();
  const { family } = useFamily();
  const familyId = family?.id ?? null;

  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);

  // Trigger numérico para forzar re-subscripción. Cuando visibilitychange
  // detecta que el tab volvió a foreground, incrementamos este valor →
  // el useEffect que tiene `[familyId, resubscribeKey]` se desuscribe y
  // se vuelve a suscribir desde cero (lee del server, no del cache stale).
  const [resubscribeKey, setResubscribeKey] = useState(0);

  // Triple defensa para casos donde el subscribe se "duerme" o queda con
  // cache stale tras restauraciones del tab:
  //   1. visibilitychange: tab background → foreground.
  //   2. pageshow event.persisted=true: la página se restaura desde BFCache
  //      (back-forward navigation o "Continue where you left off" de Chrome).
  //      visibilitychange NO se dispara en este caso porque la página
  //      "siempre estuvo visible" desde la perspectiva del documento.
  //   3. focus en window: backup adicional para navegadores que no
  //      implementan los dos anteriores correctamente.
  const wasHiddenRef = useRef(false);
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const triggerResubscribe = (reason) => {
      console.info('[HydrationGroups] resubscribe trigger:', reason);
      setResubscribeKey((k) => k + 1);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
        return;
      }
      if (document.visibilityState === 'visible' && wasHiddenRef.current) {
        wasHiddenRef.current = false;
        triggerResubscribe('visibilitychange');
      }
    };
    const onPageShow = (event) => {
      if (event.persisted) triggerResubscribe('pageshow:bfcache');
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  useEffect(() => {
    if (!familyId) {
      setGroups([]);
      setStatus('idle');
      return;
    }

    console.info('[HydrationGroups] subscribe → familyId:', familyId, 'key:', resubscribeKey);
    setStatus('loading');
    const unsubscribe = subscribeToHydrationGroups(
      familyId,
      (nextGroups) => {
        console.info('[HydrationGroups] snapshot recibido — count:', nextGroups.length);
        const sorted = [...nextGroups].sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() ?? 0;
          const bTime = b.createdAt?.toMillis?.() ?? 0;
          return aTime - bTime;
        });
        setGroups(sorted);
        setStatus('ready');
        setError(null);
      },
      (err) => {
        console.error('[HydrationGroups] subscribe error:', err);
        setError(err);
        setStatus('error');
      }
    );

    return unsubscribe;
  }, [familyId, resubscribeKey]);

  // ===== Mutations =====

  const createHydrationGroup = useCallback(
    async ({ name, hydrationIntervalHours }) => {
      if (!profile?.uid || !familyId) throw new Error('Sin sesión o sin hogar');
      return svcCreateHydrationGroup({
        uid: profile.uid,
        familyId,
        name,
        hydrationIntervalHours,
      });
    },
    [profile?.uid, familyId]
  );

  const updateHydrationGroup = useCallback(
    async (groupId, updates) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcUpdateHydrationGroup({ uid: profile.uid, groupId, updates });
    },
    [profile?.uid]
  );

  const softDeleteHydrationGroup = useCallback(
    async (groupId) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcSoftDeleteHydrationGroup({ uid: profile.uid, groupId });
    },
    [profile?.uid]
  );

  const waterHydrationGroup = useCallback(
    async (groupId, affectedPets) => {
      if (!profile?.uid) throw new Error('Sin sesión');
      return svcWaterHydrationGroup({
        uid: profile.uid,
        groupId,
        affectedPets,
      });
    },
    [profile?.uid]
  );

  const getGroupById = useCallback(
    (groupId) => groups.find((g) => g.id === groupId) ?? null,
    [groups]
  );

  const value = useMemo(
    () => ({
      groups,
      status,
      error,
      isLoading: status === 'loading',
      isEmpty: status === 'ready' && groups.length === 0,
      getGroupById,
      createHydrationGroup,
      updateHydrationGroup,
      softDeleteHydrationGroup,
      waterHydrationGroup,
    }),
    [
      groups,
      status,
      error,
      getGroupById,
      createHydrationGroup,
      updateHydrationGroup,
      softDeleteHydrationGroup,
      waterHydrationGroup,
    ]
  );

  return (
    <HydrationGroupsContext.Provider value={value}>
      {children}
    </HydrationGroupsContext.Provider>
  );
};

export const useHydrationGroups = () => {
  const ctx = useContext(HydrationGroupsContext);
  if (!ctx) {
    throw new Error(
      'useHydrationGroups must be used within a HydrationGroupsProvider'
    );
  }
  return ctx;
};
